import React, {FC, memo, useEffect, useMemo, useState} from 'react'

import ViewContextTableIcon from '@pixel/assets/icons/ViewContextTable'

import ListBase, { ListAppliedSearchFilterProps, ListSearchChangeProps, ListSearchFilterSelectSingleProps, SortOrderProps } from '@pixel/core/ListBase'

import { styled } from '@pixel/core/styles'
import useAutomationListQuery from '../hooks/useAutomationListQuery'
import { ListBaseSkeletonLoading } from '../../components/ListBaseDefaultLoading'
import AutomationsDataTable, { AutomationsDataTableColumnNames } from './AutomationsDataTable'
import { useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks'
import { AutomationSearchResult, AutomationStatusType } from '../types/Automations'
import {useQueryClient} from "@tanstack/react-query";

const TABLE_PER_PAGE_OPTIONS = [
    { value: 10 },
    { value: 25 },
    { value: 50 },
]

const CompanyAutomationsListBaseContainer = styled('section')`
    background-color: #F5F8FA;
    padding: ${({ theme }: any) => theme.spacing(6)};
    padding-top: ${({ theme }: any) => theme.spacing(2)};

    .MuiToolbar-root .MuiBox-root {
        gap: 8px;
    }
`;

const transformAppliedSearchFiltersToKeyValuePair = (appliedFilters: Array<ListAppliedSearchFilterProps> = [], ignoreValuesOfTypeAll: boolean = false): { [key: string]: any } => {
    return appliedFilters?.reduce((acc: { [key: string]: any }, curr: ListAppliedSearchFilterProps) => ({
        ...acc,
        ...{ [curr.searchFilterId]: curr.filterValues?.map((value) => value.value)?.filter((value) => !(ignoreValuesOfTypeAll && value === 'all')) }
    }), {}) || {};
};

export type AutomationSelectedCallback = (automation: AutomationSearchResult) => void

export interface CompanyAutomationsListBaseNodeContext {
    onAutomationSelected: AutomationSelectedCallback
    viewAutomationSelected: AutomationSelectedCallback
    changeAutomationStatus: AutomationSelectedCallback
}

interface CompanyAutomationsListBaseProps {
    onAutomationSelected: AutomationSelectedCallback
    viewAutomationSelected: AutomationSelectedCallback
    changeAutomationStatus: AutomationSelectedCallback
    fetchData: () => void
    refetch: boolean
}
export const CompanyAutomationsListBase: FC<CompanyAutomationsListBaseProps> = ({
    onAutomationSelected,
    viewAutomationSelected,
    changeAutomationStatus,
    fetchData,
    refetch,
}) => {
    const {
        AUTOMATION_ROWS_PER_PAGE,
        COMMON_VIEW_DATATABLE,
        AUTOMATION_SEARCH,
        AUTOMATION_LAST_UPDATED,
        AUTOMATION_STATUS,
        AUTOMATION_STATUS_ALL,
        AUTOMATION_STATUS_ACTIVE,
        AUTOMATION_STATUS_INACTIVE
    } = useMultipleLocalizedText()

    const [query, setQuery] = useState('')
    const [page, setPage] = useState(0)
    const [perPage, setPerPage] = useState(TABLE_PER_PAGE_OPTIONS[0].value)

    const searchFilterStatus: ListSearchFilterSelectSingleProps = useMemo(() => ({
        id: "status",
        label: AUTOMATION_STATUS,
        type: "single_select",
        options: [
            { value: 'all', label: AUTOMATION_STATUS_ALL },
            { value: 'ACTIVE' as AutomationStatusType, label: AUTOMATION_STATUS_ACTIVE },
            { value: 'INACTIVE' as AutomationStatusType, label: AUTOMATION_STATUS_INACTIVE }
        ]
    }), []);

    const appliedSearchFilterStatus = useMemo(() => ({
        searchFilterId: "status",
        displayLabel: AUTOMATION_STATUS + ': ' + AUTOMATION_STATUS_ALL,
        filterValues: [{ value: 'all', label: AUTOMATION_STATUS_ALL }],
    }), []);

    const searchFilters = [searchFilterStatus];
    const [appliedSearchFilters, setAppliedSearchFilters] = useState<Array<ListAppliedSearchFilterProps>>([appliedSearchFilterStatus]);

    const sortOrderLastUpdated: SortOrderProps = useMemo(() => ({
        id: "lastUpdatedDate",
        label: AUTOMATION_LAST_UPDATED,
        directions: ['desc', 'asc'],
    }), []);

    const defaultSortOrder = useMemo(() => ({
        sortOrder: sortOrderLastUpdated,
        direction: 'desc'
    }), []);

    const availableSortOrders = useMemo(() => [sortOrderLastUpdated], []);
    const [appliedSortOrder, setAppliedSortOrder] = useState(defaultSortOrder);

    const queryClient = useQueryClient()

    const {
        data,
        status,
    } = useAutomationListQuery({
        page,
        perPage,
        otherParams: {
            sortBy: appliedSortOrder?.sortOrder?.id || '',
            sortOrder: appliedSortOrder?.direction || '',
            name: query || null,
            ...transformAppliedSearchFiltersToKeyValuePair(appliedSearchFilters, true)
        }
    })

    useEffect(() => {
        if(refetch) {
            queryClient.invalidateQueries({
                queryKey: ['automations']
            })
            fetchData();
        }
    },[refetch, queryClient, fetchData])

    const nodeContext = useMemo(() => ({ onAutomationSelected, viewAutomationSelected, changeAutomationStatus }), [onAutomationSelected, viewAutomationSelected, changeAutomationStatus])

    const handleSearch = (event: ListSearchChangeProps) => {
        if (appliedSearchFilters?.length && !event.appliedSearchFilters?.length) { // Clear All is pressed.
            setQuery('');
            setAppliedSearchFilters([]);
            setAppliedSortOrder(defaultSortOrder);
        } else {
            setQuery(event.q?.trim());
            setAppliedSearchFilters(event.appliedSearchFilters);
            setAppliedSortOrder(event.appliedSortOrder);
        }
        setPage(0);
    };

    return (
        <CompanyAutomationsListBaseContainer>
            <ListBase
                showSearch
                labelSearch={AUTOMATION_SEARCH}
                labelSearchAria={AUTOMATION_SEARCH}
                searchFilters={searchFilters}
                appliedSearchFilters={appliedSearchFilters}
                appliedSearchQuery={query}
                onSearch={handleSearch}
                sortOrders={availableSortOrders}
                appliedSortOrder={appliedSortOrder}
                getRowID={(node) => node.companyAutomationId}
                pagingContext={{
                    page: page + 1,
                    perPage: perPage,
                    first: page * perPage + 1,
                    last: Math.min(page * perPage + perPage, data?.totalCount ?? 0),
                    hideFirstLastActions: true,
                }}
                rowsPerPageLabel={AUTOMATION_ROWS_PER_PAGE}
                resultsPerPage={perPage}
                perPageOptions={TABLE_PER_PAGE_OPTIONS}
                onPageChanged={(e: any, nextPage: number) => {
                    setPage(nextPage - 1);
                }}
                onPaginationChange={(e: any) => {
                    setPage(0)
                    setPerPage(e.target.value)
                }}
                results={data || { nodes: [], totalCount: 0 }}
                resultsLoading={status === 'loading'}
                nodeContext={nodeContext}
                viewContexts={[
                    {
                        ariaLabel: COMMON_VIEW_DATATABLE,
                        icon: <ViewContextTableIcon />,
                        renderResults: (viewContextProps: any) => (
                            <AutomationsDataTable
                                viewContextProps={viewContextProps}
                                dataStatus={status}
                                totalCount={data?.totalCount ?? 0}
                                onRefetch={() => {

                                }}
                                onAutomationSelected={onAutomationSelected}
                                viewAutomationSelected={viewAutomationSelected}
                                changeAutomationStatus={changeAutomationStatus}
                            />
                        ),
                        renderLoading: () => (
                            <ListBaseSkeletonLoading
                                columns={AutomationsDataTableColumnNames}
                                items={5}
                            />
                        )
                    }
                ]}
            />
        </CompanyAutomationsListBaseContainer>
    )
}

export default memo(CompanyAutomationsListBase)
